import type { FunctionComponent } from 'preact'
import { useState } from 'preact/hooks'

interface FeedbackButtonsProps {
  /** Whether a rating has already been provided */
  currentRating?: boolean | null
  /** Called when user clicks thumbs up or down */
  onRate: (rating: boolean) => void
  /** Size variant */
  size?: 'sm' | 'md'
}

/**
 * Thumbs up/down feedback buttons
 * Used for inline feedback in AI messages
 */
export const FeedbackButtons: FunctionComponent<FeedbackButtonsProps> = ({
  currentRating,
  onRate,
  size = 'md',
}) => {
  const [pendingRating, setPendingRating] = useState<boolean | null>(null)

  const handleRate = (rating: boolean) => {
    setPendingRating(rating)
    onRate(rating)
  }

  const rated = currentRating ?? pendingRating
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'
  const buttonPadding = size === 'sm' ? 'p-1' : 'p-1.5'

  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-gray-500 mr-1">Was this helpful?</span>

      {/* Thumbs Up */}
      <button
        type="button"
        onClick={() => handleRate(true)}
        disabled={rated !== null}
        className={`${buttonPadding} rounded transition-colors ${
          rated === true
            ? 'bg-green-100 text-green-600'
            : 'hover:bg-gray-200 text-gray-500 hover:text-green-600'
        } disabled:cursor-not-allowed`}
        title="Thumbs up"
      >
        <svg className={iconSize} fill={rated === true ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
          />
        </svg>
      </button>

      {/* Thumbs Down */}
      <button
        type="button"
        onClick={() => handleRate(false)}
        disabled={rated !== null}
        className={`${buttonPadding} rounded transition-colors ${
          rated === false
            ? 'bg-red-100 text-red-600'
            : 'hover:bg-gray-200 text-gray-500 hover:text-red-600'
        } disabled:cursor-not-allowed`}
        title="Thumbs down"
      >
        <svg className={iconSize} fill={rated === false ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"
          />
        </svg>
      </button>

      {rated !== null && (
        <span className="text-xs text-gray-500 ml-1">Thanks for your feedback!</span>
      )}
    </div>
  )
}
